/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { ElectricityPricePoint } from '@/lib/electricity/ElectricityPriceAdvisor';
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import { findTimeSeriesValueColumn } from '@/lib/timeSeries/timeSeriesValue';

const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

function parseTimeSeriesDate(value: string): Date | null {
    const localDateParts = LOCAL_DATE_TIME_PATTERN.exec(value.trim());

    if (!localDateParts) {
        const parsedDate = new Date(value);

        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const [, year, month, day, hour, minute, second] = localDateParts;
    const parsedDate = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
    );

    if (
        parsedDate.getFullYear() !== Number(year) ||
        parsedDate.getMonth() !== Number(month) - 1 ||
        parsedDate.getDate() !== Number(day) ||
        parsedDate.getHours() !== Number(hour) ||
        parsedDate.getMinutes() !== Number(minute) ||
        parsedDate.getSeconds() !== Number(second)
    ) {
        return null;
    }

    return parsedDate;
}

export function createElectricityPricePoints(
    parsedTimeSeries: ParsedTimeSeriesCsv | null,
    series: string,
): ElectricityPricePoint[] {
    if (!parsedTimeSeries) {
        return [];
    }

    const valueColumn = findTimeSeriesValueColumn(parsedTimeSeries, series);

    if (!valueColumn) {
        return [];
    }

    const values = parsedTimeSeries.plotYAxisValues[valueColumn] ?? [];

    return parsedTimeSeries.plotXAxisValues
        .map((timestamp, index): ElectricityPricePoint | null => {
            const parsedTimestamp = parseTimeSeriesDate(timestamp);
            const priceEurPerMwh = values[index];

            if (!parsedTimestamp || !Number.isFinite(priceEurPerMwh)) {
                return null;
            }

            return {
                priceEurPerMwh,
                timestamp: parsedTimestamp,
            };
        })
        .filter((point): point is ElectricityPricePoint => point !== null)
        .sort((left, right) => left.timestamp.getTime() - right.timestamp.getTime());
}
